import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";

const sections = [
  { id: 'about', label: 'About', num: '01' },
  { id: 'experience', label: 'Experience', num: '02' },
  { id: 'projects', label: 'Projects', num: '03' },
  { id: 'skills', label: 'Skills', num: '04' },
  { id: 'education', label: 'Education', num: '05' },
  { id: 'achievement', label: 'Achievements', num: '06' },
  { id: 'certifications', label: 'Certs', num: '07' },
  { id: 'freelancing', label: 'Freelance', num: '08' },
  { id: 'linkedin-featured', label: 'LinkedIn', num: '09' },
  { id: 'blog', label: 'Blog', num: '10' },
  { id: 'testimonials', label: 'Reviews', num: '11' },
];

export default function StickyDotNav() {
  const [activeSection, setActiveSection] = useState("about");
  const [isVisible, setIsVisible] = useState(false);
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    const onScroll = () => {
      const scrollY = window.scrollY;
      setIsVisible(scrollY > window.innerHeight * 0.8);

      // Find the active section
      let current = "about";
      for (const section of sections) {
        const el = document.getElementById(section.id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= window.innerHeight * 0.4) {
            current = section.id;
          }
        }
      }
      setActiveSection(current);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleClick = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.nav
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 20 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="fixed right-6 top-1/2 -translate-y-1/2 z-[9990] hidden lg:flex flex-col items-end gap-3"
          aria-label="Section navigation"
        >
          {sections.map((section) => {
            const isActive = activeSection === section.id;
            const isHovered = hovered === section.id;

            return (
              <button
                key={section.id}
                onClick={() => handleClick(section.id)}
                onMouseEnter={() => setHovered(section.id)}
                onMouseLeave={() => setHovered(null)}
                className="group flex items-center gap-3 relative"
                aria-label={`Navigate to ${section.label}`}
              >
                {/* Label tooltip */}
                <AnimatePresence>
                  {(isHovered || isActive) && (
                    <motion.span
                      initial={{ opacity: 0, x: 8 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: 8 }}
                      transition={{ duration: 0.2 }}
                      className={`text-[10px] font-mono uppercase tracking-wider whitespace-nowrap ${
                        isActive
                          ? "text-primary"
                          : "text-muted-foreground/60"
                      }`}
                    >
                      <span className="text-primary/40 mr-1.5">
                        {section.num}
                      </span>
                      {section.label}
                    </motion.span>
                  )}
                </AnimatePresence>

                {/* Dot */}
                <div className="relative flex items-center justify-center">
                  <div
                    className={`rounded-full transition-all duration-300 ${
                      isActive
                        ? "w-3 h-3 bg-primary shadow-[0_0_12px_rgba(255,184,0,0.4)]"
                        : "w-1.5 h-1.5 bg-muted-foreground/30 group-hover:bg-primary/60 group-hover:scale-150"
                    }`}
                  />
                  {isActive && (
                    <motion.div
                      layoutId="scroll-nav-ring"
                      className="absolute w-5 h-5 rounded-full border border-primary/30"
                      transition={{ type: "spring", stiffness: 300, damping: 25 }}
                    />
                  )}
                </div>
              </button>
            );
          })}
        </motion.nav>
      )}
    </AnimatePresence>
  );
}
